import React, { useState } from 'react';
import { Box, Typography, Button, Paper, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { EmailOutlinedIcon, UploadFileOutlinedIcon, PersonAddAlt1OutlinedIcon, ChevronLeftIcon } from 'icons/materialIcons';
import TeamV2MicrosoftIconStyled from '../components/common/TeamV2MicrosoftIconStyled';

import TeamV2AddByEmailContent from '../components/addMember/TeamV2AddByEmailContent';
import TeamV2ImportCsvContent from '../components/addMember/TeamV2ImportCsvContent';
import TeamV2CreateUnassignedContent from '../components/addMember/TeamV2CreateUnassignedContent';
import TeamV2SyncAdContent from '../components/addMember/TeamV2SyncAdContent';

const TeamV2AddMemberContainer = ({ user }) => {
    const navigate = useNavigate();
    const [selectedOption, setSelectedOption] = useState('add-email'); // Default to first option
    const [formData, setFormData] = useState(null);
    const [isValid, setIsValid] = useState(false);
    const [loading, setLoading] = useState(false);

    // Menu options configuration
    const menuOptions = [
        {
            key: 'add-email',
            title: 'Add by Email',
            description: 'Add teammates via email address',
            icon: <EmailOutlinedIcon />
        },
        {
            key: 'import-csv',
            title: 'Import via CSV',
            description: 'Add teammates via CSV file',
            icon: <UploadFileOutlinedIcon />
        },
        {
            key: 'create-members',
            title: 'Unassigned Members',
            description: 'Create members then assign to teammates later',
            icon: <PersonAddAlt1OutlinedIcon />
        },
        {
            key: 'sync-ad',
            title: 'Sync Active Directory',
            description: 'Sync teammates from Microsoft Active Directory',
            icon: <TeamV2MicrosoftIconStyled size="1.25rem" marginRight="0" />
        }
    ];

    const currentOption = menuOptions.find(opt => opt.key === selectedOption) || menuOptions[0];

    const handleBackToMembers = () => {
        navigate('/team-v2/members');
    };

    const handleMenuClick = (optionKey) => {
        setSelectedOption(optionKey);
        // Reset form data when switching options
        setFormData(null);
        setIsValid(false);
    };

    const handleDataChange = (data) => {
        setFormData(data);
    };

    const handleValidationChange = (valid) => {
        setIsValid(valid);
    };

    const renderRightContent = () => {
        switch (selectedOption) {
            case 'add-email':
                return <TeamV2AddByEmailContent onDataChange={handleDataChange} onValidationChange={handleValidationChange} />;
            case 'import-csv':
                return <TeamV2ImportCsvContent onDataChange={handleDataChange} onValidationChange={handleValidationChange} />;
            case 'create-members':
                return <TeamV2CreateUnassignedContent onDataChange={handleDataChange} onValidationChange={handleValidationChange} />;
            case 'sync-ad':
                return <TeamV2SyncAdContent onDataChange={handleDataChange} onValidationChange={handleValidationChange} />;
            default:
                return <TeamV2AddByEmailContent onDataChange={handleDataChange} onValidationChange={handleValidationChange} />;
        }
    };

    return (
        <Grid container sx={{ height: '100vh', padding: 1, overflow: 'hidden' }}>
            <Grid
                item
                xs={12}
                sx={{
                    height: '100%',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Two Column Layout */}
                <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {/* Left Sidebar - Menu Options (matching EditCardSidenav design) */}
                    <Box sx={{
                        width: '15rem',
                        backgroundColor: '#ffffff',
                        overflow: 'auto',
                        padding: '1.5rem 1rem 1rem 1.5rem',
                        borderRight: '0.0625rem solid #e0e0e0'
                    }}>
                        {/* Back to Members - Now in left panel */}
                        <Box sx={{ marginBottom: '1.25rem', paddingLeft: '0' }}>
                            <Button
                                onClick={handleBackToMembers}
                                startIcon={<ChevronLeftIcon />}
                                sx={{
                                    color: '#828282',
                                    textTransform: 'none',
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    minWidth: 'auto',
                                    marginLeft: 0,
                                    '& .MuiButton-startIcon': {
                                        marginRight: '0',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                Back to Members
                            </Button>
                        </Box>

                        <Box sx={{ marginTop: 1 }}>
                            {/* Menu List - matching EditCardSidenav style */}
                            <List>
                                {menuOptions.map((menuOption) => (
                                    <ListItem
                                        key={menuOption.key}
                                        onClick={() => handleMenuClick(menuOption.key)}
                                        sx={{
                                            paddingLeft: '1rem',
                                            paddingRight: '0.5rem',
                                            paddingTop: '0.25rem',
                                            paddingBottom: '0.25rem',
                                            whiteSpace: 'nowrap',
                                            minHeight: '2.5rem',
                                            cursor: 'pointer',
                                            borderRadius: '0.25rem',
                                            marginLeft: '0.5rem',
                                            marginBottom: '0.5rem',
                                            width: '11rem',
                                            backgroundColor: selectedOption === menuOption.key ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{
                                            minWidth: '2rem',
                                            marginRight: 0.5
                                        }}>
                                            {React.cloneElement(menuOption.icon, {
                                                sx: { fontSize: '1.25rem' }
                                            })}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={menuOption.title}
                                            primaryTypographyProps={{
                                                sx: {
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: "#4f4f4f",
                                                    marginLeft: 0
                                                }
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Box>

                    {/* Right Content Area */}
                    <Box sx={{
                        flex: 1,
                        backgroundColor: '#ffffff',
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        paddingLeft: { xs: '0.75rem', sm: '1rem', md: '1.5rem', lg: '2rem' },
                        paddingRight: { xs: '2rem', sm: '2.5rem', md: '3.5rem', lg: '4.5rem' },
                        paddingBottom: { xs: '0.5rem', sm: '1rem', md: '1.5rem', lg: '2rem' },
                        paddingTop: 0,

                    }}>
                        <Grid container>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                {renderRightContent()}
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
};

export default TeamV2AddMemberContainer;